import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  useCallback,
  type ReactNode,
} from "react";
import mqtt, { type MqttClient } from "mqtt";
import type { MqttConnectionStatus } from "../types/telemetry";

type MessageHandler = (topic: string, payload: string) => void;

interface MqttContextValue {
  status: MqttConnectionStatus;
  subscribe: (handler: MessageHandler) => () => void;
  publish: (topic: string, payload: string) => boolean;
}

const MqttContext = createContext<MqttContextValue | null>(null);

const RECONNECT_INTERVAL = 5000;

interface MqttProviderProps {
  brokerUrl: string;
  topicPrefix: string;
  children: ReactNode;
}

export function MqttProvider({
  brokerUrl,
  topicPrefix,
  children,
}: MqttProviderProps) {
  const [status, setStatus] = useState<MqttConnectionStatus>("disconnected");
  const clientRef = useRef<MqttClient | null>(null);
  const handlersRef = useRef<Set<MessageHandler>>(new Set());

  useEffect(() => {
    setStatus("connecting");

    const client = mqtt.connect(brokerUrl, {
      reconnectPeriod: RECONNECT_INTERVAL,
      keepalive: 60,
      clean: true,
      clientId: `vtms-web-${Math.random().toString(16).slice(2, 8)}`,
    });

    clientRef.current = client;

    client.on("connect", () => {
      console.log("[mqtt] connected, client.connected=", client.connected);
      setStatus("connected");
      client.subscribe(`${topicPrefix}#`, { qos: 0 });
    });

    client.on("message", (_topic, payload) => {
      const msg = payload.toString();
      for (const handler of handlersRef.current) {
        handler(_topic, msg);
      }
    });

    client.on("error", (err) => {
      console.error("[mqtt] error:", err);
      setStatus("error");
    });
    client.on("close", () => {
      console.log("[mqtt] connection closed");
      setStatus("disconnected");
    });
    client.on("reconnect", () => {
      console.log("[mqtt] reconnecting...");
      setStatus("connecting");
    });

    return () => {
      client.end(true);
      clientRef.current = null;
      setStatus("disconnected");
    };
  }, [brokerUrl, topicPrefix]);

  const subscribe = useCallback((handler: MessageHandler): (() => void) => {
    handlersRef.current.add(handler);
    return () => {
      handlersRef.current.delete(handler);
    };
  }, []);

  const publish = useCallback((topic: string, payload: string): boolean => {
    const client = clientRef.current;
    if (!client) {
      console.warn("[mqtt] publish failed: no client", topic);
      return false;
    }
    if (import.meta.env.DEV) {
      console.log(`[mqtt] publish: topic=${topic} payload=${payload} connected=${client.connected}`);
    }
    client.publish(topic, payload, { qos: 0 }, (err) => {
      if (err) {
        console.error("[mqtt] publish error:", err, topic);
      }
    });
    return true;
  }, []);

  const value = useMemo<MqttContextValue>(() => ({ status, subscribe, publish }), [status, subscribe, publish]);

  return <MqttContext.Provider value={value}>{children}</MqttContext.Provider>;
}

export function useMqttContext(): MqttContextValue {
  const ctx = useContext(MqttContext);
  if (!ctx) {
    throw new Error("useMqttContext must be used within an <MqttProvider>");
  }
  return ctx;
}
